import type { AttributeRule, Experiment } from 'types';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClient } from 'hooks/useClient';
import { apiErrorMessage } from '../api-error';

/** What PUT /experiments/:id/server-settings accepts. Send only the fields that change. */
export type ServerSettings = {
  decidedBy?: Experiment['decidedBy'];
  flagKey?: string | null;
  bucketOn?: Experiment['bucketOn'];
  attributeTargeting?: AttributeRule[];
};

export type ServerSettingsResult = Required<ServerSettings> & {
  /** True when a live experiment's change was published; false for a draft, or a failed rebuild. */
  runtimeRebuilt: boolean;
};

type UnpublishedParams = { status: Experiment['status']; result: Pick<ServerSettingsResult, 'runtimeRebuilt'> };

export const UNPUBLISHED_MESSAGE =
  'Saved, but not published to your site yet. Press Enforce new Rules, or save the change again.';

/**
 * A Running or Deployed experiment's change reaches the SDK and the browser only through the
 * rebuilt bundle. false there means the save stuck but the rebuild failed. A Draft is never
 * published, so false is expected.
 */
export const isSavedButUnpublished = ({ status, result }: UnpublishedParams) =>
  (status === 'Running' || status === 'Deployed') && !result.runtimeRebuilt;

/**
 * Writes a Server-side experiment's server settings: who assigns visitors, its flag key, what it
 * buckets on and its attribute rules. The API refuses mode changes outside Draft and
 * answers 400/409 with a sentence, which is shown as it is.
 */
export const useServerSettings = (experimentId: number) => {
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const [isSaving, setIsSaving] = useState(false);

  /** Resolves to the saved settings, or null when the API refused them (already toasted). */
  const save = async (settings: ServerSettings): Promise<ServerSettingsResult | null> => {
    setIsSaving(true);
    try {
      const result = await apiClient.put<ServerSettingsResult>(
        `/experiments/${experimentId}/server-settings`,
        settings,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', experimentId] }),
        queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] }),
      ]);
      return result;
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not save the assignment settings.'));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
};
